/**
 * Gameplay attributes for the roster. Three-free (ARCHITECTURE.md §4.1 corollary) so headless
 * combat tests can run without three.js. Visual proportions live in src/fighters/.
 *
 * ARCHITECTURE.md §16 is the source of truth for these numbers; changing one here without
 * amending §16 is a contract violation.
 */

/**
 * @typedef {object} FighterStats
 * @property {string} id
 * @property {string} prefix two-letter move-id prefix
 * @property {string} name
 * @property {string} archetype
 */

export const KESTREL = Object.freeze({
  id: 'kestrel',
  prefix: 'ky',
  name: 'Kestrel',
  archetype: 'counter-striker',
  tagline: 'Reads you, then removes you.',
  discipline: 'Vel Sarra — a hybrid of low-line kicking and close-quarters trapping',

  maxHealth: 950,
  height: 1.72,
  mass: 62,

  // Movement (m/s and f60 durations)
  walkFwdSpeed: 2.05,
  walkBackSpeed: 1.72,
  dashSpeed: 4.6,
  dashFrames: 13,
  backdashSpeed: 5.0,
  backdashFrames: 17,
  backdashInvulnFrames: 6,
  sidestepSpeed: 3.4,
  sidestepFrames: 14,
  sidestepRecoveryFrames: 7,
  crouchEnterFrames: 4,
  crouchExitFrames: 4,

  // Pushbox (capsule)
  pushboxRadius: 0.3,
  pushboxHeight: 1.66,
  crouchHeight: 1.06,
  /** Head/hurtbox top while airborne. */
  airHeight: 1.5,

  // Defence
  guardMax: 100,
  guardRegenPerSecond: 14,
  guardChipMul: 1.0,
  /** Weakness: takes extra blockstun pushback when cornered, so pressure hurts more. */
  cornerPressureMul: 1.15,

  // Unique resource: Read. Built by counter-hits, parries and whiff punishes.
  meterName: 'Read',
  meterMax: 100,
  meterStartOfRound: 0,
  meterOnCounterHit: 18,
  meterOnParry: 25,
  meterOnWhiffPunish: 14,
  meterOnDamageTaken: 0.04, // per point of damage taken
  meterDecayPerSecond: 0,

  // Unique mechanic: Flow stance
  stances: Object.freeze(['', 'flow']),
  stanceEnterFrames: 9,
  /** In Flow: faster walk, access to stance-only moves, but reduced guard integrity. */
  flowWalkMul: 1.22,
  flowGuardMul: 0.8,
  flowDurationFrames: 0, // 0 = indefinite until cancelled or hit
  /** Parry window in f60; a successful parry gives frameAdvantage below. */
  parryWindowFrames: 5,
  parryRecoveryFrames: 16,
  parryAdvantageFrames: 12,

  // Juggle behaviour when this fighter is the victim (light = floats longer)
  juggleGravityMul: 0.92,
  wallSplatThresholdMul: 1.0,

  aiProfile: Object.freeze({
    preferredRange: 1.35,
    aggression: 0.52,
    whiffPunishWeight: 1.4,
    throwWeight: 0.5,
    sidestepWeight: 1.5,
  }),
});

export const BULWARK = Object.freeze({
  id: 'bulwark',
  prefix: 'bw',
  name: 'Bulwark',
  archetype: 'power-grappler',
  tagline: 'There is no outside.',
  discipline: 'Hold Iron — pit wrestling welded to industrial-yard brawling',

  maxHealth: 1100,
  height: 1.98,
  mass: 128,

  walkFwdSpeed: 1.35,
  walkBackSpeed: 1.1,
  dashSpeed: 3.2,
  dashFrames: 19,
  backdashSpeed: 3.0,
  backdashFrames: 24,
  backdashInvulnFrames: 0,
  sidestepSpeed: 2.2,
  sidestepFrames: 19,
  sidestepRecoveryFrames: 12,
  crouchEnterFrames: 6,
  crouchExitFrames: 6,

  pushboxRadius: 0.38,
  pushboxHeight: 1.9,
  crouchHeight: 1.28,
  airHeight: 1.74,

  guardMax: 130,
  guardRegenPerSecond: 10,
  guardChipMul: 0.85,
  cornerPressureMul: 0.9,

  // Unique resource: Grit. Built by TAKING damage — the comeback lever, capped so it
  // never becomes a free win.
  meterName: 'Grit',
  meterMax: 100,
  meterStartOfRound: 0,
  meterOnCounterHit: 6,
  meterOnParry: 0,
  meterOnWhiffPunish: 5,
  meterOnDamageTaken: 0.16,
  meterDecayPerSecond: 0,

  stances: Object.freeze(['', 'anchor']),
  stanceEnterFrames: 14,
  /** In Anchor: armor frames on designated moves, slower movement, higher throw damage. */
  anchorWalkMul: 0.82,
  anchorThrowDamageMul: 1.18,
  anchorArmorHits: 1,
  anchorArmorDamageMul: 0.45,
  parryWindowFrames: 0,
  parryRecoveryFrames: 0,
  parryAdvantageFrames: 0,

  juggleGravityMul: 1.14,
  wallSplatThresholdMul: 0.88,

  aiProfile: Object.freeze({
    preferredRange: 0.85,
    aggression: 0.7,
    whiffPunishWeight: 1.0,
    throwWeight: 1.8,
    sidestepWeight: 0.5,
  }),
});

export const ROSTER = Object.freeze({ kestrel: KESTREL, bulwark: BULWARK });
export const ROSTER_IDS = Object.freeze(['kestrel', 'bulwark']);
export const PREFIX_TO_ID = Object.freeze({ ky: 'kestrel', bw: 'bulwark' });

export function getStats(id) {
  const stats = ROSTER[id];
  if (!stats) throw new Error(`Unknown fighter "${id}". Known: ${ROSTER_IDS.join(', ')}`);
  return stats;
}
